/**
 * ExcelImportDialog Component
 * 
 * Reusable dialog for importing data from Excel and PDF files.
 * Supports: Millesimi, People, Suppliers, Expenses
 */

import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, FileText, CheckCircle, AlertCircle, Download, X, Loader2, Info, AlertTriangle, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Template information for each import type
const IMPORT_CONFIGS = {
  millesimi: {
    title: 'Importa Tabella Millesimale',
    description: 'Importa i millesimi da un file Excel (.xlsx, .xls) o PDF',
    endpoint: '/api/import/millesimi',
    pdfEndpoint: '/api/import/millesimi-pdf',
    supportsPdf: true,
    columns: ['Unità', 'Millesimi'],
    notes: 'La colonna "Unità" deve corrispondere al codice interno delle unità già presenti nel sistema. I millesimi devono sommare a 1000.',
    example: 'Unità: A1, A2, B1... | Millesimi: 45.50, 52.30, 38.00...',
    pdfRequirements: [
      'Il PDF deve contenere TESTO SELEZIONABILE (non scansioni)',
      'Deve esserci una tabella con colonne Unità e Millesimi',
      'I codici unità devono corrispondere a quelli nel sistema'
    ]
  },
  units: {
    title: 'Importa Unità Immobiliari',
    description: 'Importa le unità da un file Excel (.xlsx, .xls) o PDF',
    endpoint: '/api/import/units',
    pdfEndpoint: '/api/import/units-pdf',
    supportsPdf: true,
    columns: ['Unità', 'Piano', 'Scala', 'Mq', 'Foglio', 'Particella', 'Subalterno', 'Categoria', 'Rendita', 'Note'],
    notes: 'Solo la colonna "Unità" è obbligatoria. Le unità esistenti verranno aggiornate.',
    example: 'Unità: A1, Int.1 | Piano: 1, 2 | Scala: A, B | Mq: 85.5',
    pdfRequirements: [
      'Il PDF deve contenere TESTO SELEZIONABILE (non scansioni)',
      'Deve esserci una tabella con almeno la colonna dei codici unità',
      'I dati catastali sono opzionali ma consigliati'
    ]
  },
  people: {
    title: 'Importa Anagrafica',
    description: 'Importa i dati anagrafici da un file Excel',
    endpoint: '/api/import/people',
    supportsPdf: false,
    columns: ['Nome', 'Codice Fiscale', 'Email', 'Telefono', 'Cellulare', 'PEC', 'Indirizzo', 'Città', 'CAP'],
    notes: 'Solo "Nome" è obbligatorio. Se il Codice Fiscale esiste già, i dati verranno aggiornati.',
    example: 'Nome: Mario Rossi | CF: RSSMRA80A01H501X | Email: mario@email.it'
  },
  suppliers: {
    title: 'Importa Fornitori',
    description: 'Importa l\'elenco fornitori da un file Excel',
    endpoint: '/api/import/suppliers',
    supportsPdf: false,
    columns: ['Nome', 'P.IVA', 'Codice Fiscale', 'Email', 'Telefono', 'IBAN', 'Indirizzo', 'Città', 'CAP'],
    notes: 'Solo "Nome" è obbligatorio. Se la P.IVA esiste già, i dati verranno aggiornati.',
    example: 'Nome: Elettrica SRL | P.IVA: 12345678901 | Email: info@elettrica.it'
  },
  expenses: {
    title: 'Importa Fatture/Spese',
    description: 'Importa fatture da Excel (.xlsx, .xls), PDF o XML (FatturaPA)',
    endpoint: '/api/import/expenses',
    pdfEndpoint: '/api/import/invoice-pdf',
    xmlEndpoint: '/api/import/invoice-xml',
    supportsPdf: true,
    supportsXml: true,
    columns: ['Data', 'Numero', 'Fornitore', 'Descrizione', 'Imponibile', 'IVA', 'Categoria'],
    notes: 'Campi obbligatori: Data, Fornitore, Imponibile. I fornitori mancanti verranno creati automaticamente.',
    example: 'Data: 15/01/2025 | Fornitore: Elettrica SRL | Imponibile: 500.00',
    pdfRequirements: [
      'Il PDF deve contenere TESTO SELEZIONABILE (non scansioni)',
      'Verranno estratti: Fornitore, P.IVA, Numero Fattura, Data, Importi',
      'I dati estratti richiedono una verifica manuale'
    ],
    xmlRequirements: [
      'Supporta formato FatturaPA (Fattura Elettronica Italiana)',
      'Supporta anche file .p7m (fatture firmate)',
      'I dati vengono estratti automaticamente con alta precisione'
    ]
  }
};

export const ExcelImportDialog = ({ 
  open, 
  onOpenChange, 
  importType = 'people',
  condominiumId,
  tableId = null, // For millesimi
  onSuccess
}) => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('excel'); // 'excel' or 'pdf'
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  
  const config = IMPORT_CONFIGS[importType] || IMPORT_CONFIGS.people;

  // Fetch available units for millesimi import
  useEffect(() => {
    if (open && importType === 'millesimi' && condominiumId) {
      fetchAvailableUnits();
    }
  }, [open, importType, condominiumId]);

  const fetchAvailableUnits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/import/available-units/${condominiumId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableUnits(data.units || []);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    const fileName = file.name.toLowerCase();
    const isPdf = fileName.endsWith('.pdf');
    const isXml = fileName.endsWith('.xml') || fileName.endsWith('.p7m');
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    
    if (!isPdf && !isXml && !isExcel) {
      toast.error('Formato file non supportato. Usa file .xlsx, .xls, .pdf, .xml o .p7m');
      return;
    }
    
    if (isPdf && !config.supportsPdf) {
      toast.error('L\'importazione PDF non è supportata per questo tipo di dati');
      return;
    }
    
    if (isXml && !config.supportsXml) {
      toast.error('L\'importazione XML non è supportata per questo tipo di dati');
      return;
    }
    
    setSelectedFile(file);
    setFileType(isXml ? 'xml' : (isPdf ? 'pdf' : 'excel'));
    setResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !condominiumId) return;
    
    setUploading(true);
    setResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('condominium_id', condominiumId);
      
      let endpoint;
      if (fileType === 'xml' && config.xmlEndpoint) {
        endpoint = config.xmlEndpoint;
      } else if (fileType === 'pdf' && config.pdfEndpoint) {
        endpoint = tableId ? `${config.pdfEndpoint}/${tableId}` : config.pdfEndpoint;
      } else {
        endpoint = tableId && importType === 'millesimi' 
          ? `${config.endpoint}/${tableId}` 
          : config.endpoint;
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setResult({
          success: true,
          ...data
        });
        toast.success(data.message || 'Importazione completata');
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        setResult({
          success: false,
          message: data.detail || 'Errore durante l\'importazione',
          available_units: data.available_units
        });
        toast.error(data.detail || 'Errore durante l\'importazione');
      }
    } catch (error) {
      console.error('Import error:', error);
      setResult({
        success: false,
        message: 'Errore di connessione'
      });
      toast.error('Errore di connessione');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    setShowHelp(false);
    onOpenChange(false);
  };

  const downloadTemplate = () => {
    // Create a simple CSV template that can be opened in Excel
    const headers = config.columns.join(';');
    const csvContent = `${headers}\n`;
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `template_${importType}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Template scaricato');
  };

  // Build accepted formats string
  let acceptedFormats = '.xlsx,.xls';
  if (config.supportsPdf) acceptedFormats += ',.pdf';
  if (config.supportsXml) acceptedFormats += ',.xml,.p7m';

  // Get file type icon
  const getFileIcon = () => {
    if (fileType === 'xml') return <FileText className="h-10 w-10 text-orange-600" />;
    if (fileType === 'pdf') return <FileText className="h-10 w-10 text-red-600" />;
    return <FileSpreadsheet className="h-10 w-10 text-green-600" />;
  };

  const getFileColor = () => {
    if (fileType === 'xml') return 'border-orange-500 bg-orange-50';
    if (fileType === 'pdf') return 'border-red-500 bg-red-50';
    return 'border-green-500 bg-green-50';
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            {config.title}
          </DialogTitle>
          <DialogDescription>
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Column Info - only for Excel imports */}
          {importType !== 'expenses' || fileType === 'excel' ? (
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800 mb-1">Colonne attese (Excel):</p>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {config.columns.map((col, i) => (
                      <Badge key={i} variant="outline" className="text-xs bg-white">
                        {col}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-blue-700 text-xs">{config.notes}</p>
                </div>
              </div>
            </div>
          ) : null}

          {/* XML Requirements */}
          {config.supportsXml && (
            <div className="p-3 bg-orange-50 rounded-lg">
              <div className="flex items-start gap-2">
                <FileText className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-orange-800 mb-1">Fatture XML (FatturaPA):</p>
                  <ul className="list-disc list-inside space-y-0.5 text-orange-700 text-xs">
                    {config.xmlRequirements?.map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Available Units for Millesimi */}
          {importType === 'millesimi' && availableUnits.length > 0 && (
            <div className="p-3 bg-amber-50 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 mb-1">
                    Unità disponibili nel sistema ({availableUnits.length}):
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {availableUnits.slice(0, 15).map((unit, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {unit.unit_number}
                      </Badge>
                    ))}
                    {availableUnits.length > 15 && (
                      <Badge variant="outline" className="text-xs">
                        +{availableUnits.length - 15} altre
                      </Badge>
                    )}
                  </div>
                  <p className="text-amber-700 text-xs mt-2">
                    ⚠️ I codici unità nel file devono corrispondere esattamente a quelli sopra
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* PDF Help for Millesimi */}
          {config.supportsPdf && (
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowHelp(!showHelp)}
                className="text-xs text-muted-foreground"
              >
                <HelpCircle className="h-3 w-3 mr-1" />
                {showHelp ? 'Nascondi guida PDF' : 'Come importare da PDF?'}
              </Button>
            </div>
          )}

          {showHelp && config.pdfRequirements && (
            <div className="p-3 bg-purple-50 rounded-lg">
              <div className="flex items-start gap-2">
                <FileText className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-purple-800 mb-2">Requisiti per importazione PDF:</p>
                  <ul className="list-disc list-inside space-y-1 text-purple-700 text-xs">
                    {config.pdfRequirements.map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                  <p className="text-purple-600 text-xs mt-2 italic">
                    💡 Per PDF scansionati (immagini), usare prima un software OCR esterno
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Drop Zone */}
          <div
            className={`
              border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
              ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
              ${selectedFile ? getFileColor() : ''}
            `}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedFormats}
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                {getFileIcon()}
                <div className="text-left">
                  <p className={`font-medium ${fileType === 'xml' ? 'text-orange-800' : fileType === 'pdf' ? 'text-red-800' : 'text-green-800'}`}>
                    {selectedFile.name}
                  </p>
                  <p className={`text-sm ${fileType === 'xml' ? 'text-orange-600' : fileType === 'pdf' ? 'text-red-600' : 'text-green-600'}`}>
                    {(selectedFile.size / 1024).toFixed(1)} KB
                    {fileType === 'xml' && <span className="ml-2">• XML/FatturaPA</span>}
                    {fileType === 'pdf' && <span className="ml-2">• PDF</span>}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setResult(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  Trascina il file qui oppure <span className="text-blue-600 font-medium">sfoglia</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Formati supportati: .xlsx, .xls{config.supportsPdf && ', .pdf'}{config.supportsXml && ', .xml, .p7m'}
                </p>
              </>
            )}
          </div>

          {/* Upload Progress/Result */}
          {uploading && (
            <div className="space-y-2">
              <Progress value={undefined} className="h-2" />
              <p className="text-sm text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 inline animate-spin mr-2" />
                {fileType === 'pdf' ? 'Estrazione dati dal PDF...' : 'Elaborazione in corso...'}
              </p>
            </div>
          )}

          {/* Result */}
          {result && (
            <Alert className={result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
              {result.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertTitle className={result.success ? 'text-green-800' : 'text-red-800'}>
                {result.success ? 'Importazione completata' : 'Errore'}
              </AlertTitle>
              <AlertDescription className={result.success ? 'text-green-700' : 'text-red-700'}>
                {result.message}
                
                {/* Show extracted data for PDF/XML imports */}
                {result.extracted_data && (
                  <div className="mt-3 p-2 bg-white rounded border text-xs">
                    <p className="font-medium text-gray-800 mb-2">Dati estratti:</p>
                    <div className="grid grid-cols-2 gap-1">
                      {result.extracted_data.supplier && (
                        <p><span className="text-gray-500">Fornitore:</span> {result.extracted_data.supplier}</p>
                      )}
                      {result.extracted_data.vat_number && (
                        <p><span className="text-gray-500">P.IVA:</span> {result.extracted_data.vat_number}</p>
                      )}
                      {result.extracted_data.invoice_number && (
                        <p><span className="text-gray-500">N. Fattura:</span> {result.extracted_data.invoice_number}</p>
                      )}
                      {result.extracted_data.invoice_date && (
                        <p><span className="text-gray-500">Data:</span> {result.extracted_data.invoice_date}</p>
                      )}
                      {result.extracted_data.taxable !== undefined && (
                        <p><span className="text-gray-500">Imponibile:</span> €{result.extracted_data.taxable.toFixed(2)}</p>
                      )}
                      {result.extracted_data.vat !== undefined && (
                        <p><span className="text-gray-500">IVA:</span> €{result.extracted_data.vat.toFixed(2)}</p>
                      )}
                      {result.extracted_data.total !== undefined && (
                        <p className="col-span-2 font-medium"><span className="text-gray-500">Totale:</span> €{result.extracted_data.total.toFixed(2)}</p>
                      )}
                    </div>
                    {result.needs_review && (
                      <p className="mt-2 text-amber-600 text-xs">
                        ⚠️ Verificare i dati estratti nella sezione Spese
                      </p>
                    )}
                  </div>
                )}
                
                {result.imported !== undefined && (
                  <div className="mt-2 space-y-1">
                    <p>✓ Importati: <strong>{result.imported}</strong></p>
                    {result.updated > 0 && <p>✓ Aggiornati: <strong>{result.updated}</strong></p>}
                    {result.total_found && result.total_found !== result.imported && (
                      <p className="text-amber-700">
                        📊 Trovati nel file: <strong>{result.total_found}</strong>
                      </p>
                    )}
                    {result.total_errors > 0 && (
                      <div>
                        <p className="text-amber-700">⚠ Errori: <strong>{result.total_errors}</strong></p>
                        {result.errors?.length > 0 && (
                          <ul className="text-xs mt-1 list-disc list-inside max-h-24 overflow-y-auto">
                            {result.errors.slice(0, 5).map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                            {result.errors.length > 5 && (
                              <li>...e altri {result.errors.length - 5} errori</li>
                            )}
                          </ul>
                        )}
                      </div>
                    )}
                    {result.note && (
                      <p className="text-xs text-gray-600 mt-2 italic">
                        💡 {result.note}
                      </p>
                    )}
                  </div>
                )}
                {!result.success && result.available_units && (
                  <div className="mt-2">
                    <p className="text-xs text-red-600">
                      Unità disponibili: {result.available_units.slice(0, 10).join(', ')}
                      {result.available_units.length > 10 && '...'}
                    </p>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={downloadTemplate}
            className="w-full sm:w-auto"
          >
            <Download className="h-4 w-4 mr-2" />
            Scarica Template
          </Button>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              {result?.success ? 'Chiudi' : 'Annulla'}
            </Button>
            {!result?.success && (
              <Button 
                onClick={handleUpload} 
                disabled={!selectedFile || uploading}
                className="flex-1"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importa...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Importa
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExcelImportDialog;
